/**
 * mostWantedAdmin.tsx — Admin UI for the Most Wanted database.
 *
 * List mode + Add mode (with face image, which auto-creates a linked POI
 * so the edge agent's camera detection will auto-flag this person).
 *
 * v0.4.9.2.3c22 fix #1: added edit button (Pencil icon → modal).
 *   - api.updateMostWanted (PATCH /api/most-wanted/:id) already exists.
 *   - Edit modal is metadata-only (no image swap); face gallery handles that.
 *   - Status field is included in edit (active | captured | closed).
 *   - Pencil button is gated by canWriteMw (matches API requireWrite).
 *
 * v0.4.9.2.3c22a (admin form polish):
 *   - Country field is now a strict alpha-3 SearchableSelect sourced from
 *     `src/data/countries.ts` ("Spain (ESP)" / "BLR — Belarus" pattern).
 *     Replaces the free-text Input that used to accept "Spain" or "ESP"
 *     indiscriminately.
 *   - Country pre-fills with the admin's org country (fetched via
 *     `api.getCivilRespondent(user.civil_respondent_id)` on mount).
 *   - Edit modal also uses the dropdown (pre-fills from the record's
 *     current country; updates if the admin picks a different one).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api, type BoloTargetType } from '../services/api';
import { ArrowLeft, Plus, Trash2, Upload, X, ExternalLink, ChevronDown, ChevronUp, Image as ImageIcon, Pencil } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { BoloTab } from '../components/BoloTab';
import { SearchableSelect, countryListToOptions } from '../components/SearchableSelect';
import { COUNTRIES } from '../data/countries';
import { useUserOrgCountry } from '../hooks/useUserOrgCountry';
import { FiltersBar, applyFilters, type DateRange } from '../components/FiltersBar';

const TAB_TARGET_TYPE: BoloTargetType = 'most_wanted';

const editStatusOptions = (tt: (k: string) => string): Array<{ value: 'active' | 'captured' | 'closed'; label: string }> => [
  { value: 'active', label: tt('mw.status.active') },
  { value: 'captured', label: tt('mw.status.captured') },
  { value: 'closed', label: tt('mw.status.closed') },
];

export default function MostWantedAdmin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [list, setList] = useState<any[]>([]);
  // v0.4.9.2.2: BOLO tab
  const [activeTab, setActiveTab] = useState<'records' | 'bolo'>('records');
  const [availableOrgs, setAvailableOrgs] = useState<Array<{ id: string; name: string; country: string }>>([]);
  const isAgentAdmin = user?.role === 'agentAdmin' || user?.role === 'superadmin';
  // v0.4.9.2.3c22: matches API requireWrite for PATCH /api/most-wanted/:id
  const canWriteMw = ['superadmin', 'sadmin', 'agentAdmin'].includes(user?.role ?? '');
  const currentOrgId = user?.civil_respondent_id ?? null;
  // v0.4.9.2.3c22a: pre-fill the country field on the Add form with the admin's
  // own org country (alpha-3 code). SuperAdmin typically has no org → no pre-fill.
  const orgCountry = useUserOrgCountry();
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // v0.4.9.2.3c22ca: search + date-range filter on the records tab.
  // Default = last 7 days per locked requirement. In-memory state; resets
  // on page reload, persists across tab switches within this page.
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const end = new Date(); end.setHours(23, 59, 59, 999);
    const start = new Date(); start.setDate(start.getDate() - 6); start.setHours(0, 0, 0, 0);
    return { start, end };
  });
  const [poiDetails, setPoiDetails] = useState<Record<string, any>>({});
  const [error, setError] = useState<string | null>(null);
  // Faces gallery: extra face images for multi-angle matching.
  const [faces, setFaces] = useState<Record<string, any[]>>({});
  const faceFileInputRef = useRef<HTMLInputElement | null>(null);
  const [pendingFaceMwId, setPendingFaceMwId] = useState<string | null>(null);

  // v0.4.9.2.3c22: edit modal state
  const [editing, setEditing] = useState<any | null>(null);
  const [editForm, setEditForm] = useState({
    full_name: '',
    crime_committed: '',
    country: '',
    reporting_station: '',
    wanted_in: '',
    recommended_action: '',
    red_notice_url: '',
    status: 'active' as 'active' | 'captured' | 'closed',
  });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Form state
  const [form, setForm] = useState({
    full_name: '', crime_committed: '', country: '',
    reporting_station: '', wanted_in: '', recommended_action: '', red_notice_url: '',
  });
  const [faceFile, setFaceFile] = useState<File | null>(null);
  const [facePreview, setFacePreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // v0.4.9.2.3c22a: backfill the form's country with the admin's org country
  // when the org data finishes loading. Only fires when the form's country is
  // still empty (user can opt out by picking a different value first).
  useEffect(() => {
    if (orgCountry && !form.country) {
      setForm(f => ({ ...f, country: orgCountry }));
    }
  }, [orgCountry]);

  function load() {
    setLoading(true);
    api.listMostWanted()
      .then(setList)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  async function loadPoiDetails(mwId: string, poiId: string) {
    if (poiDetails[mwId] || !poiId) return;
    // The /api/pois/:id endpoint isn't part of the new module API, but the
    // POI exists in the existing system. We hit the edge route instead:
    // /api/edge/gallery-embeddings returns the linked images for a POI.
    // For now, just show the photo_url from the most_wanted row.
    setPoiDetails(prev => ({ ...prev, [mwId]: { poi_id: poiId, image_url: list.find(x => x.id === mwId)?.photo_url || null } }));
  }

  function resetForm() {
    setForm({
      full_name: '', crime_committed: '', country: '',
      reporting_station: '', wanted_in: '', recommended_action: '', red_notice_url: '',
    });
    setFaceFile(null);
    setFacePreview(null);
    setError(null);
  }

  function onFaceFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFaceFile(file);
    const reader = new FileReader();
    reader.onload = () => setFacePreview(reader.result as string);
    reader.readAsDataURL(file);
  }

  async function submit() {
    if (!form.full_name || !form.country) {
      setError(t('records.err.nameCountryRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      let image: string | undefined;
      if (faceFile) {
        image = await fileToBase64(faceFile);
      }
      await api.createMostWanted({
        full_name: form.full_name,
        country: form.country,
        crime_committed: form.crime_committed || undefined,
        reporting_station: form.reporting_station || undefined,
        wanted_in: form.wanted_in || undefined,
        recommended_action: form.recommended_action || undefined,
        red_notice_url: form.red_notice_url || undefined,
      }, image);
      setShowAdd(false);
      resetForm();
      load();
    } catch (err: any) {
      setError(err.message || t('records.err.createFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  async function deletePerson(id: string) {
    if (!confirm(t('mw.confirmDeleteEntry'))) return;
    try {
      await api.deleteMostWanted(id);
      load();
    } catch (err: any) {
      alert('Failed: ' + err.message);
    }
  }

  // v0.4.9.2.3c22: edit modal helpers
  function openEdit(p: any) {
    setEditing(p);
    setEditForm({
      full_name: p.full_name ?? '',
      crime_committed: p.crime_committed ?? '',
      country: p.country ?? '',
      reporting_station: p.reporting_station ?? '',
      wanted_in: p.wanted_in ?? '',
      recommended_action: p.recommended_action ?? '',
      red_notice_url: p.red_notice_url ?? '',
      status: (p.status ?? 'active') as 'active' | 'captured' | 'closed',
    });
    setEditError(null);
  }

  function closeEdit() {
    setEditing(null);
    setEditError(null);
    setEditSubmitting(false);
  }

  async function submitEdit() {
    if (!editing) return;
    if (!editForm.full_name || !editForm.country) {
      setEditError('Full name and country are required');
      return;
    }
    setEditSubmitting(true);
    setEditError(null);
    try {
      await api.updateMostWanted(editing.id, {
        full_name: editForm.full_name,
        country: editForm.country,
        crime_committed: editForm.crime_committed || undefined,
        reporting_station: editForm.reporting_station || undefined,
        wanted_in: editForm.wanted_in || undefined,
        recommended_action: editForm.recommended_action || undefined,
        red_notice_url: editForm.red_notice_url || undefined,
        status: editForm.status,
      });
      closeEdit();
      load();
    } catch (err: any) {
      setEditError(err.message || t('records.err.updateFailed'));
    } finally {
      setEditSubmitting(false);
    }
  }

  async function loadFaces(mwId: string) {
    if (faces[mwId]) return;
    try {
      const f = await api.listMostWantedFaces(mwId);
      setFaces(prev => ({ ...prev, [mwId]: f }));
    } catch (_) {
      setFaces(prev => ({ ...prev, [mwId]: [] }));
    }
  }

  function startAddExtraFace(mwId: string) {
    setPendingFaceMwId(mwId);
    faceFileInputRef.current?.click();
  }

  async function onExtraFaceFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !pendingFaceMwId) return;
    const mwId = pendingFaceMwId;
    try {
      const b64 = await fileToBase64(file);
      await api.addMostWantedFace(mwId, b64);
      const f = await api.listMostWantedFaces(mwId);
      setFaces(prev => ({ ...prev, [mwId]: f }));
    } catch (err: any) {
      alert(t('records.err.addFace') + ' ' + (err.message || t('records.err.noFaceDetected')));
    } finally {
      setPendingFaceMwId(null);
      if (faceFileInputRef.current) faceFileInputRef.current.value = '';
    }
  }

  async function deleteFace(mwId: string, faceId: string) {
    if (!confirm(t('records.confirmDeleteFace'))) return;
    try {
      await api.deleteMostWantedFace(mwId, faceId);
      const f = await api.listMostWantedFaces(mwId);
      setFaces(prev => ({ ...prev, [mwId]: f }));
    } catch (err: any) {
      alert('Failed: ' + err.message);
    }
  }

  // v0.4.9.2.3c22ca: compute filtered record list (search + date range).
  // Done as a derived value in render so React re-runs it on every state change.
  // The empty-state distinguishes "no records yet" from "filter excluded all".
  const filteredList = applyFilters(
    list,
    search,
    dateRange,
    (p: any) => p.created_at,
    (p: any) =>
      [
        p.full_name,
        p.crime_committed,
        p.country,
        p.reporting_station,
        p.wanted_in,
        p.recommended_action,
        p.red_notice_url,
      ]
        .filter(Boolean)
        .join(' ')
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="px-4 py-3 bg-card border-b border-border flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/civil-respondent')} className="text-muted-foreground">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-base font-semibold text-foreground">{t('mw.title')}</h1>
        </div>
        <button
          onClick={() => setShowAdd(s => !s)}
          className="flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm hover:opacity-90"
        >
          {showAdd ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showAdd ? t('common.cancel') : t('common.add')}
        </button>
      </header>

      <main className="max-w-3xl mx-auto p-4">
        {isAgentAdmin && (
          <div className="border-b border-border flex gap-1 mb-4">
            <button
              type="button"
              onClick={() => setActiveTab('records')}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${
                activeTab === 'records'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('records.tabRecords')}
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('bolo');
                if (user?.role === 'superadmin' && availableOrgs.length === 0) {
                  api.listCivilRespondents().then((orgs: any[]) => {
                    setAvailableOrgs(
                      orgs.map((o) => ({
                        id: o.id,
                        name: o.division_name || o.name || o.id,
                        country: o.country || '',
                      }))
                    );
                  }).catch(() => {});
                }
              }}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${
                activeTab === 'bolo'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('records.tabBolo')}
            </button>
          </div>
        )}

        {activeTab === 'records' && showAdd && (
          <div className="bg-card border border-border rounded-xl p-5 shadow-sm mb-4">
            <h2 className="font-semibold text-foreground mb-1">{t('mw.newTitle')}</h2>
            <p className="text-xs text-muted-foreground mb-4">
              {t('mw.newFaceHint')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label={t('records.field.fullName')} value={form.full_name} onChange={v => setForm({ ...form, full_name: v })} />
              {/* v0.4.9.2.3c22a: strict alpha-3 country dropdown (pre-filled with org country). */}
              <SearchableSelect
                label={t('records.field.country')}
                value={form.country}
                onChange={v => setForm({ ...form, country: v })}
                options={countryListToOptions(COUNTRIES)}
                placeholder={t('records.field.searchCountry')}
                required
              />
              <Input label={t('mw.field.crimeCommitted')} value={form.crime_committed} onChange={v => setForm({ ...form, crime_committed: v })} />
              <Input label={t('records.field.reportingStation')} value={form.reporting_station} onChange={v => setForm({ ...form, reporting_station: v })} />
              <Input label={t('mw.field.wantedIn')} value={form.wanted_in} onChange={v => setForm({ ...form, wanted_in: v })} />
              <Input label={t('records.field.recommendedAction')} value={form.recommended_action} onChange={v => setForm({ ...form, recommended_action: v })} />
              <Input label={t('mw.field.redNoticeUrl')} value={form.red_notice_url} onChange={v => setForm({ ...form, red_notice_url: v })} className="md:col-span-2" />
            </div>

            <div className="mt-4">
              <label className="text-xs text-muted-foreground uppercase tracking-wide">{t('records.face.optional')}</label>
              <div className="mt-2 flex items-center gap-3">
                {facePreview ? (
                  <img src={facePreview} alt={t('records.alt.facePreview')} className="w-20 h-20 object-cover rounded" />
                ) : (
                  <div className="w-20 h-20 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground">{t('records.face.noImage')}</div>
                )}
                <label className="cursor-pointer px-3 py-2 border border-border rounded text-sm hover:bg-muted">
                  {faceFile ? t('records.face.change') : t('records.face.chooseFile')}
                  <input type="file" accept="image/*" onChange={onFaceFile} className="hidden" />
                </label>
                {faceFile && <span className="text-xs text-muted-foreground">{faceFile.name}</span>}
              </div>
            </div>

            {error && (
              <div className="mt-3 p-2 bg-destructive/10 border border-destructive text-destructive rounded text-sm">
                {error}
              </div>
            )}

            <div className="mt-4 flex gap-2">
              <button
                onClick={submit}
                disabled={submitting}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? t('common.saving') : t('common.save')}
              </button>
              <button
                onClick={() => { setShowAdd(false); resetForm(); }}
                className="px-4 py-2 border border-border rounded-lg hover:bg-muted"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'records' && (
          <>
        {loading ? (
          <div className="text-muted-foreground text-sm py-8 text-center">{t('common.loading')}</div>
        ) : list.length === 0 ? (
          <div className="text-muted-foreground text-sm py-8 text-center">{t('records.empty.mostWanted')}</div>
        ) : (
          <>
            {/* v0.4.9.2.3c22ca: search + date-range filter strip */}
            <FiltersBar
              searchValue={search}
              onSearchChange={setSearch}
              searchPlaceholder="Search by name, crime, station, country…"
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
            />
            {filteredList.length === 0 ? (
              <div className="text-muted-foreground text-sm py-8 text-center">
                {t('records.empty.noMatches')}
              </div>
            ) : (
            <div className="space-y-3">
              {filteredList.map((p) => {
              const isExpanded = expandedId === p.id;
              return (
              <div key={p.id} className="bg-card border border-border rounded-lg shadow-sm">
                <div className="p-4 flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-foreground">{p.full_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {p.country}{p.crime_committed ? ` · ${p.crime_committed}` : ''}
                      {p.reporting_station ? ` · ${p.reporting_station}` : ''}
                      {p.status !== 'active' ? ` · ${p.status}` : ''}
                    </div>
                    {p.recommended_action && (
                      <p className="text-xs text-muted-foreground mt-1 italic">{t('records.label.action')} {p.recommended_action}</p>
                    )}
                    {p.red_notice_url && (
                      <a
                        href={p.red_notice_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-primary hover:underline flex items-center gap-1 mt-1"
                      >
                        <ExternalLink className="w-3 h-3" /> {t('mw.badge.redNotice')}
                      </a>
                    )}
                    {p.poi_id && (
                      <div className="text-xs text-muted-foreground mt-1">{t('mw.badge.linkedPoi')} {p.poi_id.slice(0, 8)}…</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {p.poi_id && (
                      <button
                        onClick={() => {
                          setExpandedId(isExpanded ? null : p.id);
                          if (!isExpanded) {
                            loadPoiDetails(p.id, p.poi_id);
                            loadFaces(p.id);
                          }
                        }}
                        className="p-2 text-muted-foreground hover:bg-muted rounded"
                        title={t('records.title.viewPoiImage')}
                      >
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ImageIcon className="w-4 h-4" />}
                      </button>
                    )}
                    {/* v0.4.9.2.3c22: edit button (Pencil → modal). Gated by canWriteMw to match API requireWrite. */}
                    {canWriteMw && (
                      <button
                        onClick={() => openEdit(p)}
                        className="p-2 text-primary hover:bg-primary/10 rounded"
                        title={t('records.title.editEntry')}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => deletePerson(p.id)}
                      className="p-2 text-destructive hover:bg-destructive/10 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {isExpanded && p.poi_id && (
                  <div className="px-4 pb-4 border-t border-border pt-3">
                    <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">{t('mw.poi.imageCaption')}</div>
                    {p.photo_url ? (
                      <img
                        src={p.photo_url}
                        alt={`POI for ${p.full_name}`}
                        className="w-32 h-32 object-cover rounded border border-border"
                      />
                    ) : (
                      <div className="w-32 h-32 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground text-center p-2">
                        {t('mw.poi.none')}
                      </div>
                    )}

                    {/* Additional face gallery — multi-angle faces reduce false positives */}
                    <div className="text-xs text-muted-foreground uppercase tracking-wide mt-4 mb-2">
                      {t('records.face.gallery')}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {(faces[p.id] || []).map((f) => (
                        <div key={f.id} className="relative">
                          <img
                            src={f.photo_url}
                            alt={t('records.alt.extraFace')}
                            className="w-full aspect-square object-cover rounded"
                          />
                          <button
                            onClick={() => deleteFace(p.id, f.id)}
                            className="absolute top-1 right-1 p-1 bg-destructive text-white rounded-full"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => startAddExtraFace(p.id)}
                        className="aspect-square border-2 border-dashed border-border rounded flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-foreground"
                      >
                        <Upload className="w-5 h-5" />
                        <span className="text-xs mt-1">{t('records.face.add')}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
            )}
          </>
        )}
          </>
        )}

        {activeTab === 'bolo' && isAgentAdmin && (
          <BoloTab
            targetType={TAB_TARGET_TYPE}
            orgId={currentOrgId ?? undefined}
            availableOrgs={user?.role === 'superadmin' ? availableOrgs : undefined}
            records={list.map((r) => ({
              id: r.id,
              label: r.full_name,
              country: r.country,
              photoUrl: null,
            }))}
          />
        )}
      </main>

      {/* Hidden file input for adding extra faces to a most wanted entry. */}
      <input
        ref={faceFileInputRef}
        type="file"
        accept="image/*"
        onChange={onExtraFaceFile}
        className="hidden"
      />

      {/* v0.4.9.2.3c22: edit modal — Pencil button opens this for canWriteMw users. */}
      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={closeEdit}
        >
          <div
            className="bg-card shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3 border-b border-border flex items-center justify-between">
              <h2 className="font-semibold text-foreground">{t('mw.editTitle')}</h2>
              <button
                onClick={closeEdit}
                className="p-1 text-muted-foreground hover:bg-muted rounded"
                title={t('common.close')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  label={t('records.field.fullName')}
                  value={editForm.full_name}
                  onChange={v => setEditForm({ ...editForm, full_name: v })}
                />
                {/* v0.4.9.2.3c22a: strict alpha-3 country dropdown for edit. */}
                <SearchableSelect
                  label={t('records.field.country')}
                  value={editForm.country}
                  onChange={v => setEditForm({ ...editForm, country: v })}
                  options={countryListToOptions(COUNTRIES)}
                  placeholder={t('records.field.searchCountry')}
                  required
                />
                <Input
                  label={t('mw.field.crimeCommitted')}
                  value={editForm.crime_committed}
                  onChange={v => setEditForm({ ...editForm, crime_committed: v })}
                />
                <Input
                  label={t('records.field.reportingStation')}
                  value={editForm.reporting_station}
                  onChange={v => setEditForm({ ...editForm, reporting_station: v })}
                />
                <Input
                  label={t('mw.field.wantedIn')}
                  value={editForm.wanted_in}
                  onChange={v => setEditForm({ ...editForm, wanted_in: v })}
                />
                <Input
                  label={t('records.field.recommendedAction')}
                  value={editForm.recommended_action}
                  onChange={v => setEditForm({ ...editForm, recommended_action: v })}
                />
                <Input
                  label={t('mw.field.redNoticeUrl')}
                  value={editForm.red_notice_url}
                  onChange={v => setEditForm({ ...editForm, red_notice_url: v })}
                  className="md:col-span-2"
                />
                <div className="md:col-span-2">
                  <label className="text-xs text-muted-foreground uppercase tracking-wide">{t('records.field.status')}</label>
                  <select
                    value={editForm.status}
                    onChange={e =>
                      setEditForm({
                        ...editForm,
                        status: e.target.value as 'active' | 'captured' | 'closed',
                      })
                    }
                    className="mt-1 block w-full px-3 py-2 border border-border rounded text-sm bg-background focus:outline-none focus:border-primary"
                  >
                    {editStatusOptions(t).map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <p className="text-xs text-muted-foreground mt-4">
                  {t('mw.face.notEditable')}
              </p>

              {editError && (
                <div className="mt-3 p-2 bg-destructive/10 border border-destructive text-destructive rounded text-sm">
                  {editError}
                </div>
              )}

              <div className="mt-4 flex gap-2">
                <button
                  onClick={submitEdit}
                  disabled={editSubmitting}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 disabled:opacity-50"
                >
                  {editSubmitting ? t('common.saving') : t('records.action.saveChanges')}
                </button>
                <button
                  onClick={closeEdit}
                  className="px-4 py-2 border border-border rounded-lg hover:bg-muted"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function Input({ label, value, onChange, type = 'text', className = '' }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; className?: string;
}) {
  return (
    <div className={className}>
      <label className="text-xs text-muted-foreground uppercase tracking-wide">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="mt-1 block w-full px-3 py-2 border border-border rounded text-sm focus:outline-none focus:border-primary"
      />
    </div>
  );
}
